import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Plus, Minus, ShoppingCart, Truck, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useCart } from '../contexts/CartContext';
import { useSettings } from '../contexts/SettingsContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);

const defaultFillings = [
  { name: 'Arequipe', price: 0 },
  { name: 'Chocolate', price: 0 },
  { name: 'Frutos Rojos', price: 0 },
  { name: 'Crema de Limón', price: 0 },
];

const defaultFlavors = [
  { name: 'Vainilla', price: 0 },
  { name: 'Chocolate', price: 0 },
  { name: 'Choco Vainilla', price: 10000 },
  { name: 'Chocobrownie', price: 10000 },
  { name: 'Café Moka', price: 10000 },
  { name: 'Red Velvet', price: 10000 },
];

const isPremiumFlavor = (name) => {
  if (!name) return false;
  const n = name.toLowerCase().replace(/\s+/g, '');
  return n.includes('chocovainilla') || n.includes('chocobrownie') || n.includes('chocobrawnie') || n.includes('cafemoka') || n.includes('cafémoka') || n.includes('redvelvet');
};

const standardColors = [
  'Blanco', 'Rosado Pastel', 'Azul Pastel', 'Verde Menta', 'Lila', 'Amarillo Pastel', 'Crema', 'Personalizado (Indicar en observaciones)'
];

const defaultComplements = [
  { name: 'Vela Volcán', price: 4000 },
  { name: 'Letrero de Cumpleaños', price: 8000 },
  { name: 'Vela de Número', price: 3000 },
  { name: 'Porción de Postre Extra', price: 12000 },
];

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { whatsappNumber } = useSettings();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedFilling, setSelectedFilling] = useState(null);
  const [selectedFlavor, setSelectedFlavor] = useState(null);
  const [selectedColor, setSelectedColor] = useState('Blanco');
  const [selectedComplements, setSelectedComplements] = useState([]);
  const [observations, setObservations] = useState('');
  const [activeImage, setActiveImage] = useState('');
  const [publicConfig, setPublicConfig] = useState(null);

  useEffect(() => {
    const fetchPublicConfig = async () => {
      try {
        const { data } = await axios.get(`${API}/settings/public`);
        setPublicConfig(data);
      } catch (err) {
        console.error('Error fetching public config:', err);
      }
    };
    fetchPublicConfig();
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/products/${id}`);
        setProduct(data);
        if (data.images?.length) {
          setActiveImage(data.images[0]);
        } else {
          setActiveImage(data.image);
        }

        // Select initial values
        if (data.sizes?.length) setSelectedSize(data.sizes[0]);

        const initialFilling = data.fillings?.length ? data.fillings[0] : defaultFillings[0];
        const initialFlavor = data.flavors?.length ? data.flavors[0] : defaultFlavors[0];
        setSelectedFilling(initialFilling);
        setSelectedFlavor(initialFlavor);
      } catch (err) {
        console.error('Error fetching product:', err);
        toast.error('Producto no encontrado');
        navigate('/productos');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  useEffect(() => {
    if (selectedSize?.name?.toLowerCase().includes("30 a 35") && selectedFlavor?.is_refrigerated) {
      setSelectedFlavor(null);
      toast.error("El sabor refrigerado no está disponible para el tamaño de 30 a 35 porciones.");
    }
  }, [selectedSize, selectedFlavor]);

  if (loading) return <div className="flex justify-center py-32"><div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>;
  if (!product) return null;

  const isSpecialCategory = ['tortas-especiales', 'grados', 'comunion'].includes(product.category);
  const hasFilling = product.allow_filling ?? isSpecialCategory;
  const hasFlavor = product.allow_flavor ?? isSpecialCategory;
  const hasColor = product.allow_color ?? isSpecialCategory;

  const fillingsList = product.fillings?.length ? product.fillings : defaultFillings;
  const flavorsList = product.flavors?.length ? product.flavors : defaultFlavors;
  const complementsList = (() => {
    const list = [];
    const seen = new Set();
    const source = [
      ...(publicConfig?.complements || []),
      ...(product.complements || [])
    ];
    if (source.length === 0) return defaultComplements;
    for (const c of source) {
      if (c && c.name && !seen.has(c.name)) {
        seen.add(c.name);
        list.push(c);
      }
    }
    return list;
  })();

  const totalPrice = (selectedSize ? (selectedSize.price ?? product.base_price) : (product.base_price || 0)) +
    (hasFilling && !selectedFlavor?.is_refrigerated ? (selectedFilling?.price || 0) : 0) +
    (hasFlavor ? (selectedFlavor?.price || 0) : 0) +
    selectedComplements.reduce((s, c) => s + (c.price || 0) * (c.quantity || 1), 0);

  const deliveryDate = () => {
    let days = product.pide_hoy ? 0 : (selectedSize?.prep_days || product.preparation_days || 2);
    if (isSpecialCategory || (product.name || '').toLowerCase().includes('especial')) {
      days = Math.max(days, 3);
    }
    const now = new Date();
    const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
    const colombiaTime = new Date(utcTime + (3600000 * -5));
    if (product.pide_hoy && days === 0 && !isSpecialCategory) {
      if (colombiaTime.getHours() >= 15) {
        days = 1;
      }
    }
    colombiaTime.setDate(colombiaTime.getDate() + days);
    return colombiaTime.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const toggleComplement = (comp) => {
    setSelectedComplements(prev => {
      const exists = prev.find(c => c.name === comp.name);
      if (exists) {
        return prev.filter(c => c.name !== comp.name);
      } else {
        return [...prev, { name: comp.name, price: comp.price, quantity: 1, color: '' }];
      }
    });
  };

  const updateComplementQty = (name, delta) => {
    setSelectedComplements(prev =>
      prev.map(c => {
        if (c.name === name) {
          const newQty = Math.max(1, (c.quantity || 1) + delta);
          return { ...c, quantity: newQty };
        }
        return c;
      })
    );
  };

  const updateComplementColor = (name, color) => {
    setSelectedComplements(prev =>
      prev.map(c => {
        if (c.name === name) {
          return { ...c, color };
        }
        return c;
      })
    );
  };

  const handleAddToCart = () => {
    const finalObservations = hasColor
      ? (observations ? `Color: ${selectedColor} | ${observations}` : `Color: ${selectedColor}`)
      : observations;

    addItem(product, {
      size: selectedSize,
      filling: hasFilling && !selectedFlavor?.is_refrigerated ? selectedFilling : null,
      flavor: hasFlavor ? selectedFlavor : null,
      complements: selectedComplements,
      observations: finalObservations,
      totalPrice,
    });
    toast.success('Agregado al carrito');
    navigate('/carrito');
  };

  const steps = ['Tamano', 'Sabor', 'Relleno', 'Color', 'Adiciones', 'Resumen'];
  const availableSteps = steps.filter((s) => {
    if (s === 'Tamano' && (!product.sizes || product.sizes.length === 0)) return false;
    if (s === 'Sabor' && !hasFlavor) return false;
    if (s === 'Relleno') {
      if (!hasFilling) return false;
      if (selectedFlavor?.is_refrigerated) return false;
      return true;
    }
    if (s === 'Color' && !hasColor) return false;
    return true;
  });

  return (
    <div className="animate-fade-in" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-body text-sm text-[#75664d] hover:text-[#46371c] mb-6 transition-colors" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Product Image & Gallery */}
          <div className="space-y-4">
            <div className="rounded-2xl overflow-hidden aspect-square bg-[#f4f1ea] border border-[#e5dfd3] shadow-sm">
              <img src={activeImage || product.image} alt={product.name} className="w-full h-full object-cover transition-all duration-300 ease-in-out" />
            </div>
            {product.images && product.images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto py-1 scrollbar-thin scrollbar-thumb-[#e5dfd3]" data-testid="product-gallery">
                {product.images.map((imgUrl, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveImage(imgUrl)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all duration-200 focus:outline-none flex-shrink-0 ${(activeImage || product.image) === imgUrl
                      ? 'border-[#a0d5ba] scale-105 shadow-sm'
                      : 'border-[#e5dfd3] opacity-70 hover:opacity-100'
                      }`}
                    data-testid={`gallery-thumb-${idx}`}
                  >
                    <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}

              </div>
            )}

            {/* Warning Alert Banner with /precaucion.webp */}
            <div className="bg-[#fffbeb] border border-[#fef3c7] text-[#92400e] rounded-2xl p-4 flex flex-col gap-3 mb-6" data-testid="precaution-alert-banner">
              <img src="/precaucion.webp" alt="Precaución" className="w-full h-auto object-cover rounded-xl" />
              <div className="font-body text-xs leading-relaxed">
                <p className="font-bold text-[#b45309] mb-1">Información Importante</p>
                <p>Por favor revisa detenidamente las opciones de tamaño, rellenos y observaciones de tu torta. Si tienes alguna indicación adicional, escríbela en las observaciones al finalizar tu pedido.</p>
              </div>
            </div>
          </div>

          {/* Builder */}
          <div>
            <Badge className="bg-[#a0d5ba]/20 text-[#46371c] border-[#a0d5ba] mb-3">{product.category?.replace(/-/g, ' ')}</Badge>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#46371c] mb-2" data-testid="product-name">{product.name}</h1>
            <p className="font-body text-[#75664d] mb-4">{product.description}</p>

            {/* Delivery Alert Banner */}
            {(product.name?.toLowerCase().includes('chocoarequipe') && product.category === 'clasicas') ? (
              <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-2xl p-4 flex gap-3 items-center mb-6" data-testid="pickup-only-alert-banner">
                <Truck className="w-5 h-5 shrink-0 text-amber-800" />
                <p className="font-body text-xs leading-relaxed">
                  <span className="font-bold">Exclusivo en Sede Boston:</span> Este producto no cuenta con envío a domicilio ni retiro en otras sedes. <span className="font-bold">Solo disponible para reclamar en Sede Boston.</span>
                </p>
              </div>
            ) : (!product.delivery_methods || product.delivery_methods.includes('delivery')) ? (
              <div className="bg-[#eef6f2] border border-[#a0d5ba] text-[#347a54] rounded-2xl p-4 flex gap-3 items-center mb-6" data-testid="delivery-alert-banner">
                <Truck className="w-5 h-5 shrink-0 text-[#347a54]" />
                <p className="font-body text-xs leading-relaxed">
                  <span className="font-bold">¡Pide tu domicilio!</span> Cubre Medellín, Bello, Envigado, Itagüí, Sabaneta y Copacabana. <span className="font-bold">Desde $8.900</span>
                </p>
              </div>
            ) : (
              <div className="bg-[#fcf7ed] border border-[#e8c894] text-[#855723] rounded-2xl p-4 flex gap-3 items-center mb-6" data-testid="pickup-only-alert-banner">
                <Truck className="w-5 h-5 shrink-0 text-[#855723]" />
                <p className="font-body text-xs leading-relaxed">
                  <span className="font-bold">Exclusivo en Tienda:</span> Este producto no cuenta con envío a domicilio. <span className="font-bold">Solo disponible para recoger en tienda física.</span>
                </p>
              </div>
            )}

            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2" data-testid="builder-steps">
              {availableSteps.map((s, i) => (
                <button key={s} onClick={() => setStep(i)}
                  className={`px-3 py-1.5 rounded-full font-body text-xs font-medium whitespace-nowrap transition-colors ${step === i ? 'bg-[#46371c] text-[#fcfbf9]' : 'bg-[#f4f1ea] text-[#75664d]'}`}
                  data-testid={`step-${s.toLowerCase()}`}
                >{s}</button>
              ))}
            </div>

            {/* Step Content */}
            <div className="space-y-3 mb-6 min-h-[200px]">
              {availableSteps[step] === 'Tamano' && product.sizes?.map(size => (
                <Card key={size.name} onClick={() => setSelectedSize(size)}
                  className={`p-4 cursor-pointer border transition-all rounded-xl ${selectedSize?.name === size.name ? 'border-[#a0d5ba] bg-[#eef6f2]' : 'border-[#e5dfd3] hover:border-[#a0d5ba]/50'}`}
                  data-testid={`size-option-${size.name}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-body text-sm font-medium text-[#46371c]">{size.name}</p>
                      <p className="font-body text-xs text-[#75664d]">Listo en {size.prep_days} dia(s)</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-body text-sm font-semibold text-[#46371c]">
                        {formatPrice(size.price ?? product.base_price)}
                      </span>
                      {selectedSize?.name === size.name && <Check className="w-5 h-5 text-[#a0d5ba]" />}
                    </div>
                  </div>
                </Card>
              ))}

              {availableSteps[step] === 'Relleno' && fillingsList.map(filling => (
                <Card key={filling.name} onClick={() => setSelectedFilling(filling)}
                  className={`p-4 cursor-pointer border transition-all rounded-xl ${selectedFilling?.name === filling.name ? 'border-[#a0d5ba] bg-[#eef6f2]' : 'border-[#e5dfd3] hover:border-[#a0d5ba]/50'}`}
                  data-testid={`filling-option-${filling.name}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-body text-sm font-medium text-[#46371c]">{filling.name}</p>
                    <div className="flex items-center gap-3">
                      <span className="font-body text-sm font-semibold text-[#46371c]">{filling.price > 0 ? `+${formatPrice(filling.price)}` : 'Incluido'}</span>
                      {selectedFilling?.name === filling.name && <Check className="w-5 h-5 text-[#a0d5ba]" />}
                    </div>
                  </div>
                </Card>
              ))}

              {availableSteps[step] === 'Sabor' && (
                <div className="space-y-3">
                  <div className="bg-[#fffbeb] border border-[#fef3c7] text-[#92400e] rounded-xl p-3 text-xs mb-3 font-body">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <strong className="text-[#b45309]">Alerta de Sabores Premium:</strong> Los sabores premium son <strong>Choco Vainilla, Chocobrownie, Café Moka y Red Velvet</strong>. Se deben solicitar por medio de WhatsApp.
                      </div>
                      <a
                        href={`https://wa.me/${whatsappNumber}?text=Hola%20Keiks!%20Quisiera%20solicitar%20un%20sabor%20premium%20para%20mi%20pedido.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366] text-white text-xs font-semibold rounded-lg hover:bg-[#20bd5a] shrink-0 self-start sm:self-auto transition-colors shadow-sm"
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> Pedir x WhatsApp
                      </a>
                    </div>
                  </div>

                  {flavorsList.map(flavor => {
                    const isSize30to35 = selectedSize?.name?.toLowerCase().includes("30 a 35");
                    const isRefrigerated = flavor.is_refrigerated;
                    const isDisabled = isSize30to35 && isRefrigerated;
                    const isPrem = isPremiumFlavor(flavor.name);
                    return (
                      <Card key={flavor.name} onClick={() => { if (!isDisabled) setSelectedFlavor(flavor); }}
                        className={`p-4 border transition-all rounded-xl ${isDisabled ? 'opacity-40 cursor-not-allowed border-red-100 bg-red-50/20' : 'cursor-pointer'} ${selectedFlavor?.name === flavor.name ? 'border-[#a0d5ba] bg-[#eef6f2]' : 'border-[#e5dfd3] hover:border-[#a0d5ba]/50'}`}
                        data-testid={`flavor-option-${flavor.name}`}>
                        <div className="flex items-center justify-between">
                          <p className="font-body text-sm font-medium text-[#46371c]">
                            {flavor.name} 
                            {isPrem && <Badge className="ml-2 bg-[#b45309] text-white text-[10px]">Premium (WhatsApp)</Badge>}
                            {flavor.is_refrigerated && <Badge className="ml-2 bg-blue-100 text-blue-800 text-[10px]">Refrigerado</Badge>}
                            {isDisabled && <span className="text-red-500 text-[10px] ml-2 font-semibold">(No disponible para tamaño 30-35)</span>}
                          </p>
                          <div className="flex items-center gap-3">
                            <span className="font-body text-sm font-semibold text-[#46371c]">{flavor.price > 0 ? `+${formatPrice(flavor.price)}` : 'Incluido'}</span>
                            {!isDisabled && selectedFlavor?.name === flavor.name && <Check className="w-5 h-5 text-[#a0d5ba]" />}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}

              {availableSteps[step] === 'Color' && (
                <div className="space-y-3">
                  <p className="font-body text-sm text-[#75664d] mb-2">Selecciona el color preferido para la decoración:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {standardColors.map(color => (
                      <Card key={color} onClick={() => setSelectedColor(color)}
                        className={`p-4 cursor-pointer border transition-all rounded-xl flex items-center justify-between ${selectedColor === color ? 'border-[#a0d5ba] bg-[#eef6f2]' : 'border-[#e5dfd3] hover:border-[#a0d5ba]/50'}`}
                        data-testid={`color-option-${color.toLowerCase().replace(/\s/g, '-')}`}>
                        <span className="font-body text-sm font-medium text-[#46371c]">{color}</span>
                        {selectedColor === color && <Check className="w-4 h-4 text-[#a0d5ba]" />}
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {availableSteps[step] === 'Adiciones' && (
                <>
                  <div className="space-y-3">
                    {complementsList.map(comp => {
                      const selectedComp = selectedComplements.find(c => c.name === comp.name);
                      const isSelected = !!selectedComp;
                      return (
                        <Card key={comp.name}
                          className={`p-4 border transition-all rounded-xl ${isSelected ? 'border-[#a0d5ba] bg-[#eef6f2]' : 'border-[#e5dfd3] hover:border-[#a0d5ba]/50'}`}>
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleComplement(comp)}
                              className="rounded w-4 h-4 accent-[#a0d5ba] cursor-pointer"
                            />
                            {comp.image && (
                              <img src={comp.image} alt={comp.name} className="w-12 h-12 object-cover rounded-lg border border-[#e5dfd3] shrink-0" />
                            )}
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleComplement(comp)}>
                              <p className="font-body text-sm font-medium text-[#46371c]">{comp.name}</p>
                              <span className="font-body text-xs font-semibold text-[#75664d] block">+{formatPrice(comp.price)}</span>
                            </div>

                            {/* Quantity selector */}
                            {isSelected && (comp.allow_quantity !== false) && (
                              <div className="flex items-center gap-2 bg-white px-2 py-1 rounded-lg border border-[#e5dfd3]" onClick={e => e.stopPropagation()}>
                                <button type="button" onClick={() => updateComplementQty(comp.name, -1)} className="p-0.5 text-gray-500 hover:text-gray-700">
                                  <Minus className="w-3.5 h-3.5" />
                                </button>
                                <span className="font-body text-xs font-semibold w-5 text-center text-[#46371c]">{selectedComp.quantity || 1}</span>
                                <button type="button" onClick={() => updateComplementQty(comp.name, 1)} className="p-0.5 text-gray-500 hover:text-gray-700">
                                  <Plus className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Color selector */}
                          {isSelected && comp.allow_color && (
                            <div className="mt-3 pt-3 border-t border-[#e5dfd3]/50 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                              <label className="font-body text-xs text-[#75664d] whitespace-nowrap">Especificar Color:</label>
                              <input
                                type="text"
                                value={selectedComp.color || ''}
                                onChange={e => updateComplementColor(comp.name, e.target.value)}
                                placeholder="Ej: Rosado, Azul..."
                                className="h-8 px-2.5 rounded-lg border border-[#e5dfd3] font-body text-xs text-[#46371c] flex-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#a0d5ba]"
                              />
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                  {!['refrigeradas', 'clasicas', 'saludables'].includes(product.category) && (
                    <div className="mt-4">
                      <label className="font-body text-sm font-medium text-[#46371c] mb-2 block">Observaciones</label>
                      <textarea
                        data-testid="observations-input"
                        value={observations}
                        onChange={e => setObservations(e.target.value)}
                        placeholder="Dedicatoria, alergias, instrucciones especiales de diseño o entrega..."
                        className="w-full h-24 p-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba] resize-none"
                      />
                    </div>
                  )}
                </>
              )}

              {availableSteps[step] === 'Resumen' && (
                <Card className="p-6 border border-[#e5dfd3] rounded-2xl bg-[#eef6f2]" data-testid="order-summary">
                  <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-4">Resumen de tu pedido</h3>
                  <div className="space-y-2 font-body text-sm">
                    {selectedSize && <div className="flex justify-between"><span className="text-[#75664d]">Tamaño:</span><span className="text-[#46371c] font-medium">{selectedSize.name}</span></div>}
                    {hasFilling && selectedFilling && <div className="flex justify-between"><span className="text-[#75664d]">Relleno:</span><span className="text-[#46371c] font-medium">{selectedFilling.name}</span></div>}
                    {hasFlavor && selectedFlavor && <div className="flex justify-between"><span className="text-[#75664d]">Sabor:</span><span className="text-[#46371c] font-medium">{selectedFlavor.name}</span></div>}
                    {hasColor && <div className="flex justify-between"><span className="text-[#75664d]">Color:</span><span className="text-[#46371c] font-medium">{selectedColor}</span></div>}
                    {selectedComplements.length > 0 && (
                      <div className="flex justify-between"><span className="text-[#75664d]">Adiciones:</span><span className="text-[#46371c] font-medium">{selectedComplements.map(c => `${c.name}${c.quantity > 1 ? ` (x${c.quantity})` : ''}`).join(', ')}</span></div>
                    )}
                    {!['refrigeradas', 'clasicas', 'saludables'].includes(product.category) && observations && <div className="flex justify-between"><span className="text-[#75664d]">Observaciones:</span><span className="text-[#46371c] font-medium">{observations}</span></div>}
                    <div className="flex justify-between"><span className="text-[#75664d]">Entrega estimada:</span><span className="text-[#46371c] font-medium">{deliveryDate()}</span></div>
                    <div className="border-t border-[#e5dfd3] my-3 pt-3 flex justify-between">
                      <span className="text-[#46371c] font-semibold">Total:</span>
                      <span className="text-[#46371c] font-bold text-lg">{formatPrice(totalPrice)}</span>
                    </div>
                    <p className="text-xs text-[#75664d]">Depósito requerido (50%): {formatPrice(totalPrice * 0.5)}</p>
                  </div>
                </Card>
              )}
            </div>

            {/* Price + Navigation */}
            <div className="sticky bottom-0 bg-[#fcfbf9] pt-4 border-t border-[#e5dfd3]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-body text-xs text-[#75664d]">Precio total</p>
                  <p className="font-heading text-2xl font-bold text-[#46371c]" data-testid="total-price">{formatPrice(totalPrice)}</p>
                </div>
                <p className="font-body text-xs text-[#75664d]">Entrega: {deliveryDate()}</p>
              </div>
              <div className="flex gap-3">
                {step > 0 && (
                  <Button onClick={() => setStep(s => s - 1)} variant="outline" className="flex-1 rounded-full border-[#46371c] text-[#46371c] font-body" data-testid="prev-step-btn">
                    Anterior
                  </Button>
                )}
                {step < availableSteps.length - 1 ? (
                  <Button onClick={() => setStep(s => s + 1)} className="flex-1 bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body" data-testid="next-step-btn">
                    Siguiente
                  </Button>
                ) : (
                  <Button onClick={handleAddToCart} className="flex-1 bg-[#a0d5ba] text-[#46371c] hover:bg-[#82ba9e] rounded-full font-body font-semibold" data-testid="add-to-cart-btn">
                    <ShoppingCart className="w-4 h-4 mr-2" /> Agregar al carrito
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
